import { useEffect } from 'react';
import { useOrgPreset } from './useOrgPreset';
import { useOrganization } from '@/contexts/OrganizationContext';
import { lerOrgDaAba } from '@/contexts/orgDaAba';
import { DEFAULT_APP_TITLE, getAppTitle, setAppTitle } from '@/lib/appBrand';
import { rememberOrgPreset } from '@/lib/orgPresets';
import { applyBrandToDocument } from '@/lib/applyBrandToDocument';

/**
 * Aplica o preset de interface da org atual ao documento: `data-brand` no <html> (os tokens
 * de cor, fonte e raio ficam em src/styles/themes/), fontes da marca, favicon e nome na aba.
 *
 * Sem preset não toca em nada. Trocar de org recarrega a página (OrganizationContext), então
 * o cleanup só importa em dev/HMR, mas desfaz tudo mesmo assim.
 */
export function useApplyOrgBranding(): void {
  const preset = useOrgPreset();
  const { currentOrg } = useOrganization();

  // Guarda o preset da org aberta para a splash da próxima visita já sair com a marca certa.
  // Não na aba aberta com `?org=` (orgDaAba.ts): o cache é do navegador, e a próxima visita das
  // outras abas abre na org guardada, não na desta aba.
  useEffect(() => {
    if (currentOrg?.id && !lerOrgDaAba()) rememberOrgPreset(currentOrg.id, currentOrg.ui_preset);
  }, [currentOrg?.id, currentOrg?.ui_preset]);

  // Durante o render, não em efeito: os efeitos dos filhos (PageHeader, badge de não lidas)
  // rodam ANTES dos do AppLayout e já precisam ler o nome certo.
  setAppTitle(preset?.brandName ?? DEFAULT_APP_TITLE);

  useEffect(() => {
    if (!preset) return;
    // De novo aqui, além do render: o cleanup zera o nome, e um remount sem novo render
    // (StrictMode, Fast Refresh) voltaria com "Vertto CRM" na aba.
    setAppTitle(preset.brandName);
    const desfazer = applyBrandToDocument(preset);

    document.title = document.title.replace(DEFAULT_APP_TITLE, getAppTitle());

    return () => {
      desfazer();
      setAppTitle(DEFAULT_APP_TITLE);
    };
  }, [preset]);
}
